import {
  ErrorType,
  IIsrcMetadataConflictDetails,
  IIsrcMetadataMismatch,
  IValidationResults,
  ValidationLevel,
} from '@/data/models/validation';

type DetailsVariants = IIsrcMetadataConflictDetails & {
  Isrc?: string | null;
  Source?: string | null;
  ConflictingCatalogUpc?: string | null;
  conflictingCatalogUPC?: string | null;
  ConflictingCatalogUPC?: string | null;
  catalogUpc?: string | null;
  CatalogUpc?: string | null;
  upc?: string | null;
  UPC?: string | null;
  Mismatches?: IIsrcMetadataMismatch[];
};

type MismatchVariants = IIsrcMetadataMismatch & {
  Field?: string | null;
  FieldKey?: string | null;
  ArtistOrdinal?: number | null;
  CurrentValue?: string | null;
  OtherValue?: string | null;
};

export const IsrcMetadataMismatchFieldKey = {
  ResourceTitle: 'ResourceTitle',
  ResourceVersionTitle: 'ResourceVersionTitle',
  ResourceArtist: 'ResourceArtist',
  ResourceArtistRole: 'ResourceArtistRole',
} as const;

const sourceLabels: Record<string, string> = {
  RMSCatalog: 'RMS Catalog',
  'RMS Catalog': 'RMS Catalog',
  PRSPendingImport: 'PRS Catalog',
  PRSChangeRequestPendingImport: 'PRS Catalog',
};

function firstValue(
  ...values: Array<string | null | undefined>
): string | null {
  return values.find((value) => value && value.trim().length > 0) ?? null;
}

function normalizeMismatch(
  mismatch: IIsrcMetadataMismatch
): IIsrcMetadataMismatch {
  const variants = mismatch as MismatchVariants;

  return {
    field: firstValue(mismatch.field, variants.Field) ?? '',
    fieldKey: firstValue(mismatch.fieldKey, variants.FieldKey),
    artistOrdinal: mismatch.artistOrdinal ?? variants.ArtistOrdinal,
    currentValue: mismatch.currentValue ?? variants.CurrentValue,
    otherValue: mismatch.otherValue ?? variants.OtherValue,
  };
}

export function getIsrcMetadataConflictDetails(
  error: IValidationResults
): IIsrcMetadataConflictDetails | null {
  const details =
    error.details ??
    (
      error as IValidationResults & {
        Details?: IIsrcMetadataConflictDetails | null;
      }
    ).Details;

  if (error.errorType !== ErrorType.MetadataConflict || !details) {
    return null;
  }

  const detailVariants = details as DetailsVariants;
  const mismatches = details.mismatches ?? detailVariants.Mismatches;
  if (!Array.isArray(mismatches)) {
    return null;
  }

  return {
    ...details,
    isrc: firstValue(details.isrc, detailVariants.Isrc) ?? '',
    source: firstValue(details.source, detailVariants.Source) ?? '',
    conflictingCatalogUpc: firstValue(
      details.conflictingCatalogUpc,
      detailVariants.ConflictingCatalogUpc,
      detailVariants.conflictingCatalogUPC,
      detailVariants.ConflictingCatalogUPC,
      detailVariants.catalogUpc,
      detailVariants.CatalogUpc,
      detailVariants.upc,
      detailVariants.UPC
    ),
    mismatches: mismatches.map(normalizeMismatch),
  };
}

export function getIsrcMetadataConflictCatalogUpc(
  details: IIsrcMetadataConflictDetails,
  fallbackCatalogUpc?: string | null
): string | null {
  return firstValue(
    details.conflictingCatalogUpc,
    details.source === 'RMSCatalog' || details.source === 'RMS Catalog'
      ? fallbackCatalogUpc
      : null
  );
}

export function formatIsrcMetadataConflictSourceLabel(
  details: IIsrcMetadataConflictDetails,
  fallbackCatalogUpc?: string | null
): string {
  const label = sourceLabels[details.source] ?? details.source;
  const catalogUpc = getIsrcMetadataConflictCatalogUpc(
    details,
    fallbackCatalogUpc
  );

  return catalogUpc ? `${label} ${catalogUpc}` : label;
}

function formatMismatchValue(value?: string | null): string {
  return value ?? '""';
}

export function getTrackEditFieldForIsrcMetadataMismatch(
  mismatch: IIsrcMetadataMismatch
): string | null {
  if (mismatch.fieldKey === IsrcMetadataMismatchFieldKey.ResourceArtist) {
    return mismatch.artistOrdinal ? `Artist.${mismatch.artistOrdinal}` : null;
  }

  if (mismatch.fieldKey === IsrcMetadataMismatchFieldKey.ResourceTitle) {
    return 'Title';
  }

  if (mismatch.fieldKey === IsrcMetadataMismatchFieldKey.ResourceVersionTitle) {
    return 'Version';
  }

  const resourceArtistMatch = /^Resource Artist #(\d+)$/.exec(mismatch.field);
  if (resourceArtistMatch) {
    return `Artist.${resourceArtistMatch[1]}`;
  }

  if (mismatch.field === 'Resource Title') {
    return 'Title';
  }

  if (mismatch.field === 'Resource Version Title') {
    return 'Version';
  }

  return null;
}

export function findIsrcMetadataConflictMismatchForTrackField(
  errors: IValidationResults[],
  trackId: number | null | undefined,
  fieldName: string
): {
  details: IIsrcMetadataConflictDetails;
  mismatch: IIsrcMetadataMismatch;
} | null {
  for (const error of errors) {
    if (
      error.validationLevel !== ValidationLevel.Track ||
      error.trackId !== trackId
    ) {
      continue;
    }

    const details = getIsrcMetadataConflictDetails(error);
    if (!details) {
      continue;
    }

    const mismatch = details.mismatches.find(
      (mismatch) =>
        getTrackEditFieldForIsrcMetadataMismatch(mismatch) === fieldName
    );

    if (mismatch) {
      return { details, mismatch };
    }
  }

  return null;
}

export function formatIsrcMetadataConflictMismatchMessage(
  details: IIsrcMetadataConflictDetails,
  mismatch: IIsrcMetadataMismatch,
  fallbackCatalogUpc?: string | null
): string {
  const sourceLabel = formatIsrcMetadataConflictSourceLabel(
    details,
    fallbackCatalogUpc
  );

  return `${mismatch.field} - PRS: ${formatMismatchValue(
    mismatch.currentValue
  )} / ${sourceLabel}: ${formatMismatchValue(mismatch.otherValue)}`;
}

function formatRawValidationMessage(
  validationMessage: string,
  fallbackCatalogUpc?: string | null
): string {
  let formattedMessage = validationMessage
    .replace(/\bRMSCatalog\b/g, 'RMS Catalog')
    .replace(/\bPRSPendingImport\b/g, 'PRS Catalog')
    .replace(/\bPRSChangeRequestPendingImport\b/g, 'PRS Catalog');

  if (fallbackCatalogUpc) {
    formattedMessage = formattedMessage.replace(
      /\bwith RMS Catalog\b(?!\s+\d)/g,
      `with RMS Catalog ${fallbackCatalogUpc}`
    );
  }

  return formattedMessage;
}

export function formatIsrcMetadataConflictValidationMessage(
  error: IValidationResults,
  fallbackCatalogUpc?: string | null
): string {
  const details = getIsrcMetadataConflictDetails(error);

  if (!details) {
    return formatRawValidationMessage(
      error.validationMessage ?? '',
      fallbackCatalogUpc
    );
  }

  const sourceLabel = formatIsrcMetadataConflictSourceLabel(
    details,
    fallbackCatalogUpc
  );
  const mismatchedFields = Array.from(
    new Set(
      details.mismatches
        .map((mismatch) => mismatch.field)
        .filter((field) => field.trim().length > 0)
    )
  ).join(', ');
  const mismatchText = mismatchedFields
    ? ` Mismatched fields: ${mismatchedFields}.`
    : '';

  return `ISRC metadata conflict for "${details.isrc}" with ${sourceLabel}.${mismatchText}`;
}
